import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Bell, Calendar, Home, LogOut, MessageCircle, PawPrint, User } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { usePortalUnreadCount } from '@/queries/portal.queries';
import { cn, initials } from '@/lib/utils';

/**
 * Shell para el Pet Parent Portal. Minimalista, photo-first, mobile-first.
 * Separado del admin AppShell — sin sidebar, con tab bar inferior en móvil.
 */
export function PortalShell() {
  const portal = useAuthStore((s) => s.portal);
  const logout = useAuthStore((s) => s.logout);
  // Se cuenta en la base: el largo de la lista se quedaba en 100 como máximo.
  const unreadCount = usePortalUnreadCount().data ?? 0;
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/30 via-background to-teal-50/30">
      {/* Topbar */}
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-3xl items-center gap-3 px-4">
          {/* min-h-[44px]: es un enlace de navegación real, no solo marca.
              Ver nota en button.tsx. */}
          <Link to="/portal" className="flex min-h-[44px] items-center gap-2 min-w-0">
            {portal?.tenantLogoUrl ? (
              <img src={portal.tenantLogoUrl} alt="Logo" className="h-8 w-8 rounded-lg object-contain" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-400 text-white">
                <PawPrint className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-bold">
                {portal?.tenantName ?? 'Grooming ERP'}
              </p>
              <p className="text-[10px] text-muted-foreground">Portal de clientes</p>
            </div>
          </Link>

          {portal && (
            <div className="ml-auto flex items-center gap-1">
              <Link
                to="/portal/notifications"
                className="relative flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                aria-label="Notificaciones"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link
                to="/portal/profile"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary transition-transform hover:scale-105"
                aria-label="Mi perfil"
              >
                {initials(portal.firstName, portal.lastName)}
              </Link>
              <button
                onClick={async () => { await logout(); nav('/portal/login'); }}
                className="hidden md:inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                aria-label="Salir"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main — bottom padding leaves room for the WhatsApp FAB (~56px high
          at bottom-20) and the mobile tab bar (~57px). */}
      <main className="mx-auto max-w-3xl px-4 py-6 pb-36 md:pb-28">
        <AvisoDeClaveDeFabrica />
        <Outlet />

        {/* Quien usa el portal es el dueño de la mascota: es SU nombre, SU
            teléfono y los datos de SU perro los que están aquí. Hasta hoy no
            tenía por dónde leer la política que lo ampara — las páginas
            existían y solo se enlazaban desde el login del salón, al que él
            no entra nunca. La ley de habeas data exige que el titular pueda
            conocerla, y eso no se cumple con una página que no se alcanza. */}
        <PieLegal />
      </main>

      {/* WhatsApp FAB — sólo si el tenant configuró número */}
      {portal?.tenantWhatsappNumber && (
        <WhatsAppFab
          number={portal.tenantWhatsappNumber}
          tenantName={portal.tenantName}
          petParentName={portal.firstName}
        />
      )}

      {/* Mobile tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-20 border-t border-border bg-background/95 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-3xl">
          <TabLink to="/portal" icon={Home} label="Inicio" end />
          <TabLink to="/portal/bookings" icon={Calendar} label="Reservas" />
          <TabLink to="/portal/notifications" icon={Bell} label="Avisos" badge={unreadCount} />
          <TabLink to="/portal/profile" icon={User} label="Perfil" />
        </div>
      </nav>
    </div>
  );
}

function TabLink({
  to, icon: Icon, label, end, badge,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          // min-h-[56px] como la barra inferior del admin: con py-2, un icono
          // de 20px y una etiqueta de 10px la pestaña se queda en ~40px, por
          // debajo del mínimo táctil. Ver nota en button.tsx.
          'relative flex min-h-[56px] flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
          isActive ? 'text-primary' : 'text-muted-foreground',
        )
      }
    >
      <div className="relative">
        <Icon className="h-5 w-5" />
        {badge && badge > 0 ? (
          <span className="absolute -top-1 -right-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
            {badge > 9 ? '9+' : badge}
          </span>
        ) : null}
      </div>
      {label}
    </NavLink>
  );
}

function WhatsAppFab({
  number, tenantName, petParentName,
}: { number: string; tenantName: string; petParentName: string }) {
  const digits = number.replace(/[^\d]/g, '');
  const greeting = `Hola ${tenantName}, soy ${petParentName} 👋`;
  const href = `https://wa.me/${digits}?text=${encodeURIComponent(greeting)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Contactar ${tenantName} por WhatsApp`}
      className="fixed bottom-20 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105 active:scale-95 md:bottom-6"
    >
      <MessageCircle className="h-6 w-6" strokeWidth={2.25} />
      <span className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
      </span>
    </a>
  );
}

/**
 * El aviso de que sigue entrando con su teléfono como contraseña.
 *
 * Va en el marco, no en el perfil: quien entra al perfil ya está mirando la
 * configuración. Hay que alcanzar a quien nunca entra ahí, que es casi todo
 * el mundo — un dueño abre el portal para ver a su perro, no para revisar
 * ajustes.
 *
 * Desaparece solo cuando la cambia. No se puede cerrar: si se pudiera, se
 * cerraría el primer día y no volvería a verse nunca.
 */
function AvisoDeClaveDeFabrica() {
  const portal = useAuthStore((s) => s.portal);
  if (!portal || portal.passwordChangedAt) return null;

  return (
    <Link
      to="/portal/profile"
      className="mb-4 flex min-h-[44px] items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 transition-colors hover:bg-amber-100"
    >
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <span>
        <strong>Tu contraseña es tu número de teléfono.</strong>{' '}
        Cualquiera que lo sepa puede ver a tu mascota. Toca aquí para cambiarla.
      </span>
    </Link>
  );
}

/**
 * El pie legal del portal.
 *
 * Discreto a propósito: quien abre esto viene a ver a su perro, no a leer
 * condiciones. Pero tiene que estar, y tiene que estar en todas las
 * pantallas — no escondido en el perfil, que es justo donde no entra.
 */
function PieLegal() {
  return (
    <footer className="mt-10 border-t border-border pt-5 text-center">
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <Link to="/politica-tratamiento-datos" className="min-h-[24px] hover:text-foreground hover:underline">
          Tratamiento de datos
        </Link>
        <Link to="/terminos-y-condiciones" className="min-h-[24px] hover:text-foreground hover:underline">
          Términos y condiciones
        </Link>
      </nav>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Sistema de{' '}
        <a
          href="https://netcloudnetworking.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          Netcloud Networking
        </a>
      </p>
    </footer>
  );
}
